/**
 * Simple Form Service Helper
 * Factory function for creating form services without complex transformations
 * Use when form data maps 1:1 with API DTOs
 */

import { BaseFormService, ValidationRules } from './base-form-service';

/**
 * Options for creating a simple form service
 */
export interface SimpleFormServiceOptions<TRecord, TFormData> {
    basePath: string | readonly string[];
    initialData: Partial<TFormData>;
    validationRules?: ValidationRules<TFormData>;
    normalizeRecord?: (raw: any) => TRecord;
}

/**
 * Create a simple form service with identity transformations
 * Use when no complex data transformation is needed between form and API
 *
 * @example
 * ```ts
 * const formService = createSimpleFormService({
 *   basePath: '/lot-categories',
 *   initialData: { name: '', active: true },
 *   validationRules: {
 *     name: [ValidationRules.required('Nome é obrigatório')]
 *   }
 * });
 * ```
 */
export function createSimpleFormService<TRecord = any, TFormData = any>(options: SimpleFormServiceOptions<TRecord, TFormData>): BaseFormService<TRecord, TFormData> {
    return new BaseFormService<TRecord, TFormData>({
        basePath: options.basePath,
        initialData: options.initialData,
        validationRules: options.validationRules as any,
        normalizeRecord: options.normalizeRecord,
        // Identity transformations - no data mapping needed
        transformForCreate: (form: TFormData) => form,
        transformForUpdate: (form: Partial<TFormData>, _id: string | number) => form,
        transformToForm: (record) => record as any,
    });
}

/**
 * Simple form service class for direct instantiation or extension
 * Alternative to the factory function when you need a class
 */
export class SimpleFormService<TRecord = any, TFormData = any> extends BaseFormService<TRecord, TFormData> {
    constructor(options: SimpleFormServiceOptions<TRecord, TFormData>) {
        super({
            basePath: options.basePath,
            initialData: options.initialData,
            validationRules: options.validationRules as any,
            normalizeRecord: options.normalizeRecord,
            transformForCreate: (form: TFormData) => form,
            transformForUpdate: (form: Partial<TFormData>, _id: string | number) => form,
            transformToForm: (record) => record as any,
        });
    }
}
